import { HostSurface, OpenAppParams } from 'app/shared/types/open-app-params.types';
import { isFresherToken, isSameSession } from 'app/shared/utils/jwt';
import qs from 'qs';

const LOCAL_STORAGE_KEY = 'openAppParams';

const HOST_SURFACES: ReadonlySet<string> = new Set<HostSurface>(['popover', 'fullscreen']);

/**
 * Only a surface this build knows. An unrecognised value degrades to `undefined`, which is the
 * mobile shape — so a host that starts sending a surface we have never heard of behaves as every
 * host did before the param existed, rather than falling into a branch written for something else.
 */
const readHostSurface = (value: unknown): HostSurface | undefined =>
  (typeof value === 'string' && HOST_SURFACES.has(value) ? (value as HostSurface) : undefined);

export const useOpenParams = (): OpenAppParams => {
  const routeParams = qs.parse(window.location.search, { ignoreQueryPrefix: true });
  const params = {
    authToken: routeParams.authToken as string || '',
    apiUrl: routeParams.apiUrl as string || '',
    locale: routeParams.locale as string || 'en',
    buildingUuid: routeParams.buildingUuid as string || '',
    appBrand: routeParams.appBrand as string || '',
    /*
     * Spread conditionally so an absent surface is an ABSENT key, not `undefined` — this object is
     * JSON-stringified into storage, and `JSON.stringify` drops undefined values anyway. Writing it
     * explicitly keeps the stored shape and the in-memory shape the same thing.
     */
    ...(readHostSurface(routeParams.hostSurface) ? { hostSurface: readHostSurface(routeParams.hostSurface) } : {}),
  };
  // Guarded: a corrupted/tampered openAppParams entry must not throw on every render and
  // brick boot — fall back to the URL launch params instead.
  let stored: OpenAppParams | null = null;
  try {
    const storedParams = localStorage.getItem(LOCAL_STORAGE_KEY);
    stored = storedParams ? (JSON.parse(storedParams) as OpenAppParams) : null;
  } catch {
    stored = null;
  }

  /*
   * `hostSurface` is deliberately NOT part of this check. Every host sends the five below; only the
   * tenant portal sends a surface, so requiring it would make every mobile launch look
   * param-less and fall back to storage.
   *
   * It is what keeps the stored surface from ever contradicting the live one, too: the portal always
   * sends the full five, so a fresh mount always resolves from the URL and rewrites the store. The
   * store is a same-mount reload cache (the SDK's init-timeout `location.replace` strips the query),
   * never a value inherited from the other surface.
   */
  const hasFullLaunchParams = Boolean(
    routeParams.authToken && routeParams.apiUrl && routeParams.appBrand && routeParams.buildingUuid && routeParams.locale,
  );

  if (hasFullLaunchParams) {
    // The webview is launched once with a token in the URL and is never re-navigated,
    // so on any reload (or the SDK's init-timeout location.replace) the query-param
    // token can be OLDER than one useTokenRefresh already refreshed and stored. Prefer the
    // stored token ONLY when it's the SAME session (sub + sessionUuid) AND genuinely
    // fresher — never combine a prior session's leftover token with this launch's context.
    const keepStored = Boolean(stored?.authToken)
      && isSameSession(stored?.authToken, params.authToken)
      && isFresherToken(stored?.authToken, params.authToken);
    const authToken = keepStored ? (stored as OpenAppParams).authToken : params.authToken;
    const resolved = { ...params, authToken };
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(resolved));

    return resolved;
  }

  return stored ?? params;
};

// Persist a refreshed token so a hard reload (which re-reads localStorage) uses it.
export const updateStoredAuthToken = (authToken: string): void => {
  try {
    const storedParams = localStorage.getItem(LOCAL_STORAGE_KEY);

    if (!storedParams) {
      return;
    }

    const parsed = JSON.parse(storedParams);
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({ ...parsed, authToken }));
  } catch {
    // Storage unavailable/malformed — in-memory token state is the source of truth.
  }
};
